import { Formik } from 'formik';
import qs from 'qs';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { IXtimeStandardService } from '../../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../../context/Appointment/appointment';
import { Types, useCart, useCartDispatch } from '../../../context/cart';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { useHideKioskBackground } from '../../../context/Page/page';
import { CTAButton } from '../../../controls/ButtonBase';
import { Disclaimer } from '../../../controls/Disclaimer/Disclaimer';
import { Heading } from '../../../controls/Heading/Heading';
import { SearchListResult } from '../../../controls/SearchListResults/SearchListResult/SearchListResult';
import { KeyboardLayouts } from '../../../controls/SimpleKeyboard/Layout';
import { Spacer } from '../../../controls/Spacer/Spacer';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { useTabFocusRefContext } from '../../../controls/Tabs/Tabs';
import { Tab, Tabs } from '../../../controls/Tabs/UncontrolledTabs';
import { formatAsCurrency } from '../../../helpers/Number';
import { Xtime } from './XTime';
import { default as css } from './XTime.module.scss';
import { nameof } from '../../../helpers/nameof';
import { GMFormTextArea } from '../../../controls/TextField';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { GMForm } from '../../../controls/Form';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';

export interface IProps {
    onProceed: () => void;
    onError: () => void;
}

export const XtimeServices: React.FC<IProps> = React.memo((props) => {
    useHideKioskBackground()();
    const location = useLocation();
    const Localization = useLocalization();
    const appointment = useAppointmentState();
    const kiosk = useKioskState();
    const cart = useCart();
    const cartDispatch = useCartDispatch();
    const xtime = new Xtime(kiosk.key, kiosk.dealer.key);
    const [services, setServices] = React.useState<Record<IXtimeStandardService['type'], IXtimeStandardService[]>>();
    const setAppointment = useAppointmentUpdateAndPersist();
    const [hasError, setHasError] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
    const [activeTabIndex, setActiveTabIndex] = React.useState(parsed.showCommentsTab === 'true' ? 1 : 0);

    React.useEffect(() => {
        if (!services && !hasError && !loading) {
            getServices();
        }
    });

    React.useEffect(() => {
        if (activeTabIndex !== 0) {
            cartDispatch({ type: Types.ClearSnackbar });
        }
    }, [activeTabIndex, cartDispatch]);

    const getServices = async () => {
        setLoading(true);
        const cachedServices = [];
        if (!cachedServices.length) {
            try {
                const xtimeServices = (await xtime.getServices(appointment.vin, appointment.make ?? ''))?.filter(
                    (service) => service.reservableByCustomer && (service.type === 'repair' || service.type === 'maintenance'),
                );

                if (xtimeServices?.length) {
                    setServices(
                        xtimeServices
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .reduce(
                                (prev, curr) => {
                                    prev[curr.type].push(curr);

                                    return prev;
                                },
                                { maintenance: [],
                                    repair: [] } as Record<IXtimeStandardService['type'], IXtimeStandardService[]>,
                            ),
                    );
                }
                else {
                    setHasError(true);
                    props.onError();

                    return;
                }
            }
            catch (err) {
                console.warn(err);
                setHasError(true);
                props.onError();

                return;
            }
        }
        setLoading(false);
    };

    const toggleItem = async (service: IXtimeStandardService) => {
        cartDispatch({
            type: Types.ToggleXtimeService,
            payload: {
                id: service.id,
                goMotoId: service.goMotoId,
                type: 'standard',
                name: service.name,
                opCode: service.dmsOpcode,
                price: service.price,
                description: service.description,
                isCommonRepair: service.type === 'repair',
            },
        });
    };

    const onProceed = async ({ comments }: { comments: string; }) => {
        if (activeTabIndex !== 2) {
            setActiveTabIndex(activeTabIndex + 1);
            document.querySelector('div[class^=ScrollableBody]')!.scrollTop = 1;
        }
        else {
            cartDispatch({ type: Types.UpdateComment,
                payload: { key: 'Services',
                    comment: comments } });
            const updated: typeof appointment = {
                ...appointment,
                comment: cart.comments.map((c) => c.comment)
                    .join('; '),
                xtimeServices: cart.xtime.services.map(({ type, menuId, ...rest }) => rest),
            };
            const isMobileCheckIn = !isElectron() && !!appointment.mobileCheckin;
            await setAppointment(updated, false, false, false, true, undefined, isMobileCheckIn, false, isMobileCheckIn);
            props.onProceed();
        }
    };

    if (!services || loading) {
        return <Spinner />;
    }

    const initialValues = {
        comments: appointment.comment || '',
    };

    // Contents of the comments tab needed to be extracted so the ref is used within its provider
    const CommentsTab = (props: { name: string; }) => {
        const tabFocusRefContext = useTabFocusRefContext();

        return (
            <>
                <h4 className={css.noHeaderStyle}>{Localization.IsThereAnythingElseYouWouldLikeToTellUs}</h4>
                {
                    <GMFormTextArea
                        name={props.name}
                        id={Localization.TypeAddiitionalComments}
                        title={Localization.TypeAddiitionalComments}
                        label={''}
                        ariaLabel={Localization.TypeAddiitionalComments}
                        placeholder={Localization.TypeAddiitionalComments}
                        tabFocusRefContext={tabFocusRefContext}
                        keyboardLayout={KeyboardLayouts.Alphanumeric}
                        shiftAfterPeriod
                        autoFocus
                    />
                }
            </>
        );
    };

    return (
        <Formik initialValues={initialValues} onSubmit={onProceed}>
            <GMForm
                heading={
                    <Heading
                        primaryHeading={
                            <GMAppointmentName />
                        }
                        secondaryHeading={Localization.DoesYourVehicleNeedAdditionalServices}
                        leftAlign
                    />
                }
                submitButton={(
                    <CTAButton
                        styleType={'primary'}
                        type={'submit'}
                        title={Localization.ClickToProceedToNextStep}
                        label={Localization.Proceed}
                        id={Localization.ClickToProceedToNextStep}
                        ariaLabel={Localization.ClickToProceedToNextStep}
                    />
                )}
                fullWidthKioskInputs
            >
                <Tabs activeTabIndex={activeTabIndex} onTabChange={setActiveTabIndex}>
                    <Tab
                        tabIndex={0}
                        label={Localization.VehicleMaintenance}
                        hideKeyboard
                        active={activeTabIndex === 0}
                    >
                        <h4 className={css.serviceHeader}>{Localization.RecommendedMaintenance}</h4>
                        {services.maintenance.map((s) => (
                            <React.Fragment key={s.id}>
                                <XtimeServiceItem service={s} onToggle={toggleItem} />
                                <Spacer />
                            </React.Fragment>
                        ))}
                    </Tab>
                    <Tab
                        tabIndex={1}
                        label={Localization.VehicleRepairs}
                        hideKeyboard
                        active={activeTabIndex === 1}
                    >
                        <h4 className={css.serviceHeader}>{Localization.IndividualServices}</h4>
                        {services.repair.map((s) => (
                            <React.Fragment key={s.id}>
                                <XtimeServiceItem service={s} onToggle={toggleItem} />
                                <Spacer />
                            </React.Fragment>
                        ))}
                    </Tab>
                    <Tab
                        tabIndex={2}
                        label={Localization.TypeMyRequirements}
                        active={activeTabIndex === 2}
                    >
                        <CommentsTab name={nameof<typeof initialValues>('comments')} />
                    </Tab>
                </Tabs>
                <Disclaimer />
            </GMForm>
        </Formik>
    );
});

interface IXtimeServiceItemProps {
    service: IXtimeStandardService;
    onToggle: (service: IXtimeStandardService) => Promise<void>;
}

const XtimeServiceItem: React.FC<IXtimeServiceItemProps> = React.memo((props) => {
    const Localization = useLocalization();
    const [showDescription, setShowDescription] = React.useState(false);
    const [checked, setChecked] = React.useState(false);

    const {
        xtime: { services },
    } = useCart();

    React.useEffect(() => {
        const serviceChecked = services.findIndex((s) => s.type === 'standard' && s.id === props.service.id) > -1;
        if (checked !== serviceChecked) {
            setChecked(serviceChecked);
        }
    }, [services, checked, props.service.id]);

    const onToggle = async () => {
        await props.onToggle(props.service);
    };

    const toggleDescription = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
        setShowDescription(!showDescription);

        return false;
    };

    return (
        <SearchListResult
            title={props.service.name}
            headerLevel={5}
            checked={checked}
            onClick={onToggle}
        >
            {showDescription && <div>{props.service.description}</div>}
            <div className={css.content}>
                {props.service.description?.length && (
                    <div className={css.detailsButton} onClick={toggleDescription}>
                        {showDescription ? Localization.HideDetails : Localization.ShowDetails}
                    </div>
                )}
                <div className={css.price}>{formatAsCurrency(props.service.price, true)}</div>
            </div>
        </SearchListResult>
    );
});
